import { Fragment, useState, type ReactNode } from 'react';
import { useMeContext } from '../app/MeContext';
import { useSheet, useToast } from '../app/shell-context';
import { ApiError } from '../api/client';
import { formatDayYear } from '../lib/time';
import { formatKop } from '../lib/money';
import { pluralWithCount } from '../lib/plural';
import { Notice, Opt, Pill } from '../ui/parts';
import { useSubscriptionMutations } from './api';
import type { ChangeQuote, PlanInfo, Subscription } from './types';

/**
 * Тариф организации. Оплата уходит в ЮKassa, поэтому итог любого действия —
 * либо мгновенная смена (когда остатка хватило), либо ссылка на оплату.
 * Ссылку показываем кнопкой, а не открываем сами: внутри Telegram окно,
 * открытое из кода, гасится клиентом, и человек остаётся ни с чем.
 */

/** Описание тарифа приходит строкой, и перевод строки в ней — намеренный. */
function lines(text: string): ReactNode {
  return text.split('\n').map((line, i) => (
    <Fragment key={line}>{i > 0 ? <br /> : null}{line}</Fragment>
  ));
}

function PayLink({ url, note }: { url: string; note?: string }) {
  return (
    <>
      {note ? <Notice kind="calm">{note}</Notice> : null}
      <a className="btn solid" href={url} target="_blank" rel="noreferrer">Перейти к оплате</a>
    </>
  );
}

/** Отмена. Доступ живёт до конца оплаченного периода — это главное, что надо сказать. */
export function CancelSubscriptionSheet({ subscription }: { subscription: Subscription }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const { cancel } = useSubscriptionMutations();
  const until = subscription.current_period_end;

  return (
    <>
      <h3>Отменить подписку</h3>
      <p>
        {until
          ? `Подписка будет действовать до окончания оплаченного периода — до ${formatDayYear(until, me.center.tz)}.`
          : 'Подписка будет действовать до окончания оплаченного периода.'}
      </p>
      <p>
        После этого организация перейдёт в режим просмотра: существующие записи и напоминания
        сохранятся, но создавать новые записи будет нельзя. Возобновить подписку можно в любой момент.
      </p>
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Сохранить подписку</button>
        <button
          className="btn solid-danger"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate(undefined, {
            onSuccess: () => { toast('Подписка отменена'); sheet.close(); },
            onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось отменить'),
          })}
        >
          Отменить подписку
        </button>
      </div>
    </>
  );
}

/**
 * Смена тарифа. Неиспользованный остаток текущего периода идёт в зачёт:
 * хватает — переходим сразу и кладём излишек днями сверху, не хватает —
 * просим доплату. Обе цифры показываем до нажатия, а не после.
 */
export function ChangePlanSheet({ quotes, current }: { quotes: ChangeQuote[]; current: string }) {
  const me = useMeContext();
  const sheet = useSheet();
  const toast = useToast();
  const { change } = useSubscriptionMutations();
  const [plan, setPlan] = useState<string | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);

  const chosen = quotes.find((q) => q.plan === plan) ?? null;

  if (payUrl && chosen) {
    return (
      <>
        <h3>Доплата за переход</h3>
        <p>
          Тариф «{chosen.label}» — {formatKop(chosen.priceKop)}. В зачёт идёт остаток текущего
          периода {formatKop(chosen.creditKop)}, к оплате {formatKop(chosen.dueKop)}.
        </p>
        <PayLink url={payUrl} note="Тариф сменится, как только ЮKassa подтвердит оплату." />
      </>
    );
  }

  return (
    <>
      <h3>Изменить тариф</h3>
      <p>
        Неиспользованный остаток текущего тарифа идёт в зачёт. Если его хватает — переход
        бесплатный, а излишек добавится днями к новому сроку.
      </p>
      {quotes.map((q) => (
        <Opt
          key={q.plan}
          pressed={plan === q.plan}
          disabled={q.plan === current}
          title={q.label}
          aside={<Pill kind="off">{q.plan === current ? 'сейчас' : q.dueKop > 0 ? `+${formatKop(q.dueKop)}` : 'без доплаты'}</Pill>}
          note={q.plan === current
            ? `Текущий тариф, ${formatKop(q.priceKop)}`
            : `${formatKop(q.priceKop)} · до ${formatDayYear(q.periodEnd, me.center.tz)}${
              q.bonusDays > 0 ? ` (${pluralWithCount(q.bonusDays, 'бонусный день', 'бонусных дня', 'бонусных дней')} из зачёта)` : ''}`}
          onClick={() => setPlan(q.plan)}
        />
      ))}
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!chosen || change.isPending}
          onClick={() => chosen && change.mutate(chosen.plan, {
            onSuccess: (res) => {
              if (res.applied) {
                toast('Тариф изменён');
                sheet.close();
                return;
              }
              // Доплата: остаёмся в шторке и показываем ссылку на оплату.
              if (res.confirmationUrl) setPayUrl(res.confirmationUrl);
              else toast('Не получилось создать платёж');
            },
            onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сменить тариф'),
          })}
        >
          {chosen && chosen.dueKop > 0 ? `Доплатить ${formatKop(chosen.dueKop)}` : 'Перейти'}
        </button>
      </div>
    </>
  );
}

/** Первая оплата: демо-период, отменённая или просроченная подписка. */
export function ChoosePlanSheet({ plans }: { plans: Record<string, PlanInfo> }) {
  const sheet = useSheet();
  const toast = useToast();
  const { pay } = useSubscriptionMutations();
  const [plan, setPlan] = useState<string | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);

  const entries = Object.entries(plans);
  const chosen = plan ? plans[plan] : null;

  if (payUrl && chosen) {
    return (
      <>
        <h3>Оплата тарифа</h3>
        <p>«{chosen.label}» — {formatKop(chosen.priceKop)}.</p>
        <PayLink url={payUrl} note="Подписка включится, как только ЮKassa подтвердит оплату." />
      </>
    );
  }

  return (
    <>
      <h3>Выберите тариф</h3>
      <p>
        Оплата разовая, без автопродления. По окончании периода вы сами решаете, продлевать
        подписку или нет.
      </p>
      {entries.map(([code, p]) => (
        <Opt
          key={code}
          pressed={plan === code}
          title={`${p.label} — ${formatKop(p.priceKop)}`}
          note={lines(p.description)}
          onClick={() => setPlan(code)}
        />
      ))}
      <div className="row-actions">
        <button className="btn" onClick={() => sheet.close()}>Отмена</button>
        <button
          className="btn solid"
          disabled={!plan || pay.isPending}
          onClick={() => plan && pay.mutate(plan, {
            onSuccess: (res) => setPayUrl(res.confirmationUrl),
            onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось создать платёж'),
          })}
        >
          Оплатить
        </button>
      </div>
    </>
  );
}
